import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCurrency, parseLocalizedNumber, round } from "../helpers";

const DynamicRevenueSharing = ({
  rows,
  setRows,
  units,
  unitId,
  isAccountant,
  isAdmin,
  isManager,
  total,
}) => {
  const [alert, setAlert] = useState({ type: "", message: "" });
  const phantomNameRef = useRef(null);
  const rowPercentRef = useRef(null);
  const rowEuroRef = useRef(null);
  const rowNameRef = useRef(null);
  const suppressSelectRef = useRef(false);
  const createdRef = useRef(false);

  const handleRowChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const getOptions = (index) => {
    if (index === null)
      return units.filter(
        (unit) => !rows?.map((row) => row.unit)?.includes(unit.unit_id) ?? true
      );

    return units.filter(
      (unit) =>
        rows[index].unit === unit.unit_id ||
        !rows.map((row) => row.unit).includes(unit.unit_id)
    );
  };
  const handlePercentChange = (index, value) => {
    const updatedRows = [...rows];

    // Calculate euro based on percent
    if (value === "") {
      updatedRows[index]["percent"] = "";
      updatedRows[index]["euro"] = "";
      updatedRows[index]["percentDisplay"] = "";
      updatedRows[index]["euroDisplay"] = "";
      setRows(updatedRows);
      return;
    }

    const parsed = parseLocalizedNumber(value);
    if (!isNaN(parsed)) {
      updatedRows[index]["percent"] = parsed;
      updatedRows[index]["euro"] = round((parsed / 100) * total, 2);
      updatedRows[index]["percentDisplay"] = value;
      updatedRows[index]["euroDisplay"] = formatCurrency(
        (parsed / 100) * total
      );
      setRows(updatedRows);
    }
  };

  const handleEuroChange = (index, value) => {
    const updatedRows = [...rows];

    if (value === "") {
      updatedRows[index]["percent"] = "";
      updatedRows[index]["euro"] = "";
      updatedRows[index]["percentDisplay"] = "";
      updatedRows[index]["euroDisplay"] = "";
      setRows(updatedRows);
      return;
    }
    // Calculate percent based on euro
    const parsed = parseLocalizedNumber(value);
    if (!isNaN(parsed)) {
      updatedRows[index]["euroDisplay"] = value;
      updatedRows[index]["percentDisplay"] = formatCurrency(
        (parsed / total) * 100
      );
      updatedRows[index]["euro"] = parsed;
      updatedRows[index]["percent"] = round(parsed / total, 10) * 100;

      setRows(updatedRows);
    }
  };

  const handleRemoveRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const commitPhantomRow = (unitId, percent, euro, queued) => {
    const updatedRows = [...rows];
    if (unitId !== null) {
      percent = 0;
      euro = 0;
      updatedRows.push({
        unit: unitId,
        percent: 0,
        euro: 0,
        percentDisplay: "",
        euroDisplay: "",
      });
      setRows(updatedRows);
    } else if (percent !== null) {
      let parsed = parseLocalizedNumber(percent);
      if (isNaN(parsed)) parsed = 0;
      if (queued) {
        const lastRow = updatedRows[updatedRows.length - 1];
        lastRow.percent = 10 * lastRow.percent + parsed;
        lastRow.percentDisplay = 10 * lastRow.percentDisplay + parsed;
        lastRow.euro = round((lastRow.percent / 100) * total, 2);
        lastRow.euroDisplay = formatCurrency((lastRow.percent / 100) * total);
      } else {
        updatedRows.push({
          unit: unitId,
          percent: parsed,
          euro: round((parsed / 100) * total, 2),
          percentDisplay: parsed,
          euroDisplay: formatCurrency((parsed / 100) * total),
        });
      }
      setRows(updatedRows);
    } else if (euro !== null) {
      let parsed = parseLocalizedNumber(euro);
      if (isNaN(parsed)) parsed = 0;
      if (queued) {
        const lastRow = updatedRows[updatedRows.length - 1];
        lastRow.euro = 10 * lastRow.euro + parsed;
        lastRow.euroDisplay = 10 * lastRow.euroDisplay + parsed;
        lastRow.percent = round((lastRow.euro / total, 10) * 100);
        lastRow.percentDisplay = formatCurrency((lastRow.euro / total) * 100);
      } else {
        updatedRows.push({
          unit: unitId,
          percent: round((parsed / total, 10) * 100),
          euro: parsed,
          percentDisplay: formatCurrency((parsed / total) * 100),
          euroDisplay: parsed,
        });
      }
      setRows(updatedRows);
    }
  };

  const handleBlurRound = (index) => {
    const updatedRows = [...rows];
    let percent = parseLocalizedNumber(updatedRows[index].percent);
    let euro = parseLocalizedNumber(updatedRows[index].euro);
    if (isNaN(percent)) percent = 0;
    if (isNaN(euro)) euro = 0;
    if (percent < 0) percent = 0;
    if (euro < 0) euro = 0;
    if (euro > total) euro = parseFloat(total);
    if (total_percent > 100) {
      percent -= total_percent - 100;
      euro = round((percent / 100) * total, 2);
      setAlert({
        type: "warning",
        message: "Total over EBS revenue",
      });
    }
    updatedRows[index].percentDisplay = formatCurrency(percent);
    updatedRows[index].euroDisplay = formatCurrency(euro);
    updatedRows[index].percent = round(percent, 10);
    updatedRows[index].euro = round(euro, 2);
    setRows(updatedRows);
  };

  const total_percent =
    rows?.reduce((acc, row) => {
      const value = parseFloat(row.percent);
      return acc + (isNaN(value) ? 0 : value);
    }, 0) ?? 0;

  /*   const total_euro =
    rows?.reduce((acc, row) => {
      const value = parseFloat(row.euro);
      return acc + (isNaN(value) ? 0 : value);
    }, 0) ?? 0; */

  useEffect(() => {
    const val = 100;
    if ((rows?.length ?? 0) === 0 || rows[0].unit == null) {
      setRows([
        {
          unit: unitId ?? null,
          percent: round(val, 10),
          euro: total === "" ? 0 : total,
          percentDisplay: formatCurrency(val),
          euroDisplay: total === "" ? "0,00" : formatCurrency(total),
        },
      ]);
    } else if (rows[0].unit !== unitId) {
      const updatedRows = [...rows];
      const index = updatedRows.findIndex((row) => row.unit === unitId);
      if (index !== -1) {
        updatedRows.splice(index, 1);
      }
      updatedRows[0].unit = unitId;
      setRows(updatedRows);
    } else {
      const updatedRows = [...rows];
      updatedRows.map((row) => {
        row.percentDisplay = formatCurrency(parseFloat(row.percent));
        row.euro = round((row.percent * total) / 100, 2);
        row.euroDisplay = formatCurrency((row.percent * total) / 100);
        return row;
      });
      setRows(updatedRows);
    }
  }, [unitId, total]);

  useEffect(() => {
    if ((rows?.length ?? 0) === 0) return;
    const updatedRows = [...rows];
    const percent = updatedRows[0].percent - total_percent + 100;
    if (percent < 0) {
      updatedRows[0] = {
        unit: updatedRows[0].unit,
        percent: 0,
        euro: 0,
        percentDisplay: "0,00",
        euroDisplay: "0,00",
      };
      setRows(updatedRows);
      return;
    }
    const percentDisplay = formatCurrency(percent);
    const eur = round((percent * total) / 100, 2);
    const euroDisplay = formatCurrency((percent * total) / 100);
    updatedRows[0] = {
      unit: updatedRows[0].unit,
      percent: percent,
      euro: eur,
      percentDisplay: percentDisplay,
      euroDisplay: euroDisplay,
    };
    setRows(updatedRows);
  }, [total_percent]);

  return (
    <Box
      sx={{
        position: "relative",
        border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
        borderRadius: 1,
        padding: 2,
        marginTop: 4,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          top: 0,
          left: 8,
          transform: "translateY(-50%)",
          backgroundColor: "white",
          paddingX: 0.5,
          color: "rgba(0, 0, 0, 0.6)", // MUI label default
          fontSize: "0.75rem",
          lineHeight: 1,
        }}
      >
        EBS Revenue Sharing
      </Typography>

      {/* Existing Rows */}
      {rows &&
        rows.map((row, index) => {
          const availableOptions = getOptions(index);
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: 2,
                width: "100%",
              }}
            >
              <Autocomplete
                disabled={index === 0}
                options={availableOptions}
                getOptionLabel={(option) => option.unit_name || ""}
                isOptionEqualToValue={(option, value) =>
                  option.unit_id === value.unit_id
                }
                value={
                  availableOptions.find((u) => u.unit_id === row.unit) || null
                }
                onChange={(e, newValue) => {
                  handleRowChange(
                    index,
                    "unit",
                    newValue ? newValue.unit_id : ""
                  );
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.target.blur();
                    setTimeout(() => {
                      phantomNameRef.current?.focus();
                    }, 0);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select unit"
                    size="small"
                    fullWidth
                    inputRef={rowNameRef}
                  />
                )}
                sx={{
                  /* 
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: index === 0 ? "none" : "default",
                  }, */
                  width: "50%",
                }}
              />

              <TextField
                disabled={index === 0}
                placeholder="Value"
                type="text"
                value={row.percentDisplay || ""}
                onFocus={(e) => {
                  if (suppressSelectRef.current) {
                    suppressSelectRef.current = false;
                    createdRef.current = false;
                    return;
                  }
                  e.target.select();
                }}
                onChange={(e) => handlePercentChange(index, e.target.value)}
                onBlur={() => {
                  handleBlurRound(index);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.target.blur();
                    setTimeout(() => {
                      phantomNameRef.current?.focus();
                    }, 0);
                  }
                }}
                size="small"
                inputProps={{
                  maxLength: 6,
                  min: 0,
                  max: 100,
                  step: "any",
                  style: { textAlign: "right" },
                }}
                sx={{
                  /*
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: index === 0 ? "none" : "default",
                  }, */
                  width: "20%",
                }}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <span>&nbsp;%</span>
                    </Box>
                  ),
                }}
                inputRef={rowPercentRef}
              />
              <TextField
                disabled={index === 0}
                placeholder="Value"
                value={row.euroDisplay || ""}
                onFocus={(e) => {
                  if (suppressSelectRef.current) {
                    suppressSelectRef.current = false;
                    createdRef.current = false;
                    return;
                  }
                  e.target.select();
                }}
                onChange={(e) => handleEuroChange(index, e.target.value)}
                onBlur={() => {
                  handleBlurRound(index);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.target.blur();
                    setTimeout(() => {
                      phantomNameRef.current?.focus();
                    }, 0);
                  }
                }}
                size="small"
                inputProps={{
                  min: 0,
                  step: "any",
                  maxLength: 16,
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <span>€&nbsp;</span>
                    </Box>
                  ),
                }}
                sx={{
                  /* 
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: index === 0 ? "none" : "default",
                  }, */
                  width: index !== 0 ? "25%" : "30%",
                }}
                inputRef={rowEuroRef}
                maxLength={16}
              />

              {index !== 0 && (
                <IconButton
                  onClick={() => handleRemoveRow(index)}
                  sx={{
                    width: "3%",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "#f5f5f5", // subtle hover
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          );
        })}

      {/* Phantom Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          opacity: 0.5,
          width: "100%",
        }}
      >
        <Autocomplete
          options={getOptions(null)}
          getOptionLabel={(option) => option.unit_name || ""}
          isOptionEqualToValue={(option, value) =>
            option.unit_id === value.unit_id
          }
          value={null}
          onChange={(event, newValue) => {
            commitPhantomRow(
              newValue ? newValue.unit_id : "",
              null,
              null,
              false
            );
            setTimeout(() => {
              rowNameRef.current?.focus();
            }, 10);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select unit"
              size="small"
              fullWidth
              inputRef={phantomNameRef}
            />
          )}
          sx={{ width: "50%" }}
        />

        <TextField
          placeholder="Value"
          // type="decimal"
          size="small"
          value={""}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            if (createdRef.current) {
              commitPhantomRow(null, e.target.value, null, true);
              return;
            }
            createdRef.current = true;
            commitPhantomRow(null, e.target.value, null, false);
            suppressSelectRef.current = true;
            setTimeout(() => {
              rowPercentRef.current?.focus();
            }, 0);
          }}
          inputProps={{
            min: 0,
            max: 100,
            step: "any",
            style: { textAlign: "right" },
          }}
          sx={{ width: "20%" }}
          InputProps={{
            endAdornment: <span style={{ marginLeft: 4 }}>%</span>,
          }}
        />
        <TextField
          placeholder="Value"
          // type="decimal"
          size="small"
          value={""}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            if (createdRef.current) {
              commitPhantomRow(null, null, e.target.value, true);
              return;
            }
            createdRef.current = true;
            commitPhantomRow(null, null, e.target.value, false);
            suppressSelectRef.current = true;
            setTimeout(() => {
              rowEuroRef.current?.focus();
            }, 0);
          }}
          inputProps={{
            min: 0,
            step: "any",
          }}
          InputProps={{
            startAdornment: <span style={{ marginLeft: 4 }}>€&nbsp;</span>,
          }}
          sx={{ width: "30%" }}
        />
      </Box>

      <Divider sx={{ my: 2, backgroundColor: "rgba(0, 0, 0, 0.23)" }} />

      {/* Total Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          marginTop: 2,
          marginLeft: 1,
          width: "100%",
        }}
      >
        <Typography width="50%">Total</Typography>

        <Typography width="15%" textAlign="end" noWrap display="block">
          {formatCurrency(100) || ""} %
        </Typography>
        <Box width="8%"></Box>
        <Typography width="27%" noWrap>
          €&nbsp;{formatCurrency(total) || ""}
        </Typography>
      </Box>
      <Snackbar
        open={alert.message !== ""}
        autoHideDuration={4000}
        onClose={() => setAlert({ type: "", message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={alert.type || "info"}
          elevation={0}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DynamicRevenueSharing;
